/**
 * TanStack Query hooks for DTA collaboration (comments, proposals).
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  dtaCollaborationApi,
  type CreateCommentRequest,
  type CreateProposalRequest,
} from "@/services/dtaCollaborationApi"

const collaborationKeys = {
  all: ["collaboration"] as const,
  // Mutations invalidate `comments` (all versions); reads use `commentsForVersion`. Invalidating
  // the narrow key would miss other cached versions.
  comments: (dtaId: string) => [...collaborationKeys.all, "comments", dtaId] as const,
  commentsForVersion: (dtaId: string, version: string) =>
    [...collaborationKeys.comments(dtaId), version] as const,
  proposals: (dtaId: string) => [...collaborationKeys.all, "proposals", dtaId] as const,
  proposalsForVersion: (dtaId: string, version: string) =>
    [...collaborationKeys.proposals(dtaId), version] as const,
}

/**
 * Fetch a DTA version's comments, refreshing every 30s.
 */
export function useComments(dtaId: string, version: string) {
  return useQuery({
    queryKey: collaborationKeys.commentsForVersion(dtaId, version),
    queryFn: () => dtaCollaborationApi.getComments(dtaId, undefined, version),
    enabled: Boolean(dtaId) && Boolean(version),
    refetchInterval: 30_000,
  })
}

/**
 * Create a comment and invalidate the comments cache.
 */
export function useCreateComment(dtaId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: CreateCommentRequest) =>
      dtaCollaborationApi.createComment(dtaId, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: collaborationKeys.comments(dtaId) })
    },
  })
}

/**
 * Resolve, reject, or reply to a comment and invalidate the comments cache.
 */
export function useUpdateComment(dtaId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      commentId: string
      status: "PENDING" | "RESOLVED" | "REJECTED"
      resolutionComment?: string
    }) =>
      dtaCollaborationApi.updateComment(dtaId, vars.commentId, vars.status, vars.resolutionComment),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: collaborationKeys.comments(dtaId) })
    },
  })
}

/**
 * Fetch a DTA version's proposals, refreshing every 30s.
 */
export function useProposals(dtaId: string, version: string) {
  return useQuery({
    queryKey: collaborationKeys.proposalsForVersion(dtaId, version),
    queryFn: () => dtaCollaborationApi.getProposals(dtaId, undefined, undefined, version),
    enabled: Boolean(dtaId) && Boolean(version),
    refetchInterval: 30_000,
  })
}

/**
 * Create a proposal and invalidate the proposals cache.
 */
export function useCreateProposal(dtaId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: CreateProposalRequest) =>
      dtaCollaborationApi.createProposal(dtaId, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: collaborationKeys.proposals(dtaId) })
    },
  })
}

/**
 * Accept or reject a proposal and invalidate the proposals cache.
 */
export function useReviewProposal(dtaId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      proposalId: string
      status: "ACCEPTED" | "REJECTED" | "MODIFIED"
      reviewComment?: string
    }) =>
      dtaCollaborationApi.updateProposal(dtaId, vars.proposalId, vars.status, vars.reviewComment),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: collaborationKeys.proposals(dtaId) })
    },
  })
}
